import { Box, Flex } from '@chakra-ui/react';
import { ReactComponent as TextColorPickerIcon } from 'assets/icons/text_color_picker.svg';
import { ReactComponent as TransparentIcon } from 'assets/icons/transparent.svg';
import { useTranslation } from 'react-i18next';

import { TRIGGER_BUTTON_SIZE } from '../ColorPicker.config';
import { DefaultTriggerProps } from '../ColorPicker.types';
import { getBackgroundGradient, getBackgroundGradientSize } from './DefaultTrigger.helpers';

const COLOR_ICON_SIZE = 5;
const FOCUS_STYLE = { boxShadow: 'var(--vg-shadows-outline)', outline: 'none' };
const HOVER_STYLE = { bg: 'hover.gray' };

const triggerColorTestId = 'colorpicker-trigger-color';
const colorPickerTriggerTestId = 'colorpicker-button';

export const DefaultTrigger = ({
  iconStyle,
  color,
  iconColors,
  triggerButtonRef,
  label,
  onClick,
  onBlur,
  onFocus,
}: DefaultTriggerProps) => {
  const isBorder = iconStyle === 'border';
  const isText = iconStyle === 'text';
  const isStep = iconStyle === 'step';

  // the trigger button can have a single color or multiple colors
  const hasMultipleColorIcon = iconColors && iconColors.length > 0;

  const backgroundSize = hasMultipleColorIcon ? getBackgroundGradientSize(iconColors.length) : undefined;
  const backgroundImage = hasMultipleColorIcon ? getBackgroundGradient(iconColors) : undefined;

  const { t } = useTranslation('editor_color_picker', { useSuspense: false });
  const ariaDescription = t('colorPicker.description', { color });

  return (
    <Flex
      w={isStep ? 'full' : TRIGGER_BUTTON_SIZE}
      h={isStep ? 'full' : TRIGGER_BUTTON_SIZE}
      borderRadius={'sm'}
      as={'button'}
      // Any added here to allow the ref to be passed to the button as there is a mismatch between the types when as is used
      ref={triggerButtonRef as any}
      transition={'opacity 0.2s'}
      _focus={FOCUS_STYLE}
      _hover={HOVER_STYLE}
      justifyContent={'center'}
      alignItems={'center'}
      data-testid={colorPickerTriggerTestId}
      onClick={onClick}
      onBlur={onBlur}
      onFocus={onFocus}
      aria-label={label}
      aria-describedby={ariaDescription}
    >
      {isText ? (
        <>
          <Flex mt='-5px' align='center' justify='center' direction='column'>
            <TextColorPickerIcon fill={color} />
            <Flex
              mt='-11px'
              w='18px'
              h='5px'
              borderRadius='lg'
              backgroundRepeat='no-repeat'
              backgroundImage={backgroundImage}
              backgroundSize={backgroundSize}
              background={hasMultipleColorIcon ? undefined : color}
              borderColor='black-alpha-500'
              border='1px solid'
            />
          </Flex>
        </>
      ) : (
        <Flex
          w={isStep ? 'full' : COLOR_ICON_SIZE}
          h={isStep ? 'full' : COLOR_ICON_SIZE}
          borderRadius={isStep ? 0 : 'sm'}
          backgroundRepeat={'no-repeat'}
          backgroundImage={backgroundImage}
          backgroundSize={backgroundSize}
          background={hasMultipleColorIcon ? undefined : color}
          border={'1px solid var(--vg-colors-black-alpha-500)'}
          borderWidth={isStep ? 0 : undefined}
          justifyContent={'center'}
          alignItems={'center'}
          position={'relative'}
          data-testid={triggerColorTestId}
        >
          <Box zIndex={'-1'}>
            <TransparentIcon />
          </Box>
          {isBorder && (
            <Box
              position={'absolute'}
              w={3}
              h={3}
              bg='white'
              boxShadow='0px 0px 0px 1px var(--vg-colors-black-alpha-500) inset'
            />
          )}
        </Flex>
      )}
    </Flex>
  );
};
